import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Sparkles, Heart } from 'lucide-react';
import affirmationFemale from '@/assets/affirmation-character-preview.jpg';
import affirmationMale from '@/assets/affirmation-preview-male.jpg';
import affirmationNeutral from '@/assets/affirmation-preview-neutral.jpg';

const previews = [
  { src: affirmationFemale, alt: 'Affirmation character — female silhouette with "I love ME"' },
  { src: affirmationMale, alt: 'Affirmation character — male portrait with "I love ME"' },
  { src: affirmationNeutral, alt: 'Affirmation character — abstract energy figure with "I love ME"' },
];

export function SelfAffirmationSection() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % previews.length), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="py-16 md:py-24">
      <div className="container max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          {/* Text column */}
          <motion.div
            className="text-center md:text-left"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold billboard-flash mb-4 !leading-tight">
              It starts with you.
            </h2>
            <p className="text-lg md:text-xl text-secondary font-medium mb-3">
              Before you pour into others, pour into yourself.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-8 max-w-md mx-auto md:mx-0">
              What you say to yourself matters just as much as what you say to others.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              <Link to="/affirmation-images">
                <Button size="lg" className="gap-2 rounded-xl shadow-lg shadow-primary/20">
                  <Sparkles className="h-4 w-4" />
                  Create Your Affirmation
                </Button>
              </Link>
              <Link to="/try">
                <Button size="lg" variant="outline" className="gap-2 rounded-xl">
                  <Heart className="h-4 w-4" />
                  Start Your First Message
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Visual column — rotating previews */}
          <motion.div
            className="flex justify-center md:justify-end"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative w-56 md:w-64 lg:w-72">
              <div className="rounded-2xl overflow-hidden border border-border/40 shadow-xl shadow-primary/10 aspect-[4/5]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={index}
                    src={previews[index].src}
                    alt={previews[index].alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={640}
                    height={800}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                  />
                </AnimatePresence>
              </div>
              {/* Small label */}
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-card border border-border/50 rounded-full px-4 py-1.5 shadow-sm">
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                  Your Affirmation Character
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
